/**
 * home.js
 * All JavaScript for the Home page.
 * Bundled via Vite → outputs to dist/home.bundle.js
 */

import './scroll-stack-section.jsx';
import { initPanoramicScroll } from './panoramic-scroll-section.js';
import { initJitterScrollSection } from './jitter-scroll-section.js';
import { initJitterMobileHero } from './jitter-mobile-hero.js';
import { initJitterMobileAbout } from './jitter-mobile-about.js';
import { initRiveScrollSection } from './rive-scroll-section.js';
import { initSiteLoader } from './site-loader.js';
import { initSiteHeaderAudio } from './site-header-audio.js';
import { initHomeLinksNewTab } from './home-links-new-tab.js';
import { initHomeContactForm } from './home-contact-form.js';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

initSiteLoader();
initSiteHeaderAudio();
initHomeLinksNewTab();
initHomeContactForm();

// Hero is optional: never gate horizontal scroll on #heroSection.
if (document.querySelector('.home-page')) {

    initPanoramicScroll();
    initRiveScrollSection();
    initJitterScrollSection();
    initJitterMobileHero();
    initJitterMobileAbout();

    gsap.registerPlugin(ScrollTrigger);

    // ------------------------------------------------------------------------
    // DOM References
    // ------------------------------------------------------------------------
    const banner = document.getElementById('heroBanner');
    const section = document.getElementById('heroSection');
    const fadeEl = document.querySelector('.hero-fade');
    const introPanel = document.getElementById('introPanel');
    const hSection = document.getElementById('horizontalSection');
    const hFrame = document.getElementById('horizontalFrame');
    const hTrack = document.getElementById('horizontalTrack');
    const hScrubberFill = document.getElementById('hScrubberFill');


    // ------------------------------------------------------------------------
    // Focal Points
    // ------------------------------------------------------------------------
    const SCALE = 2;

    const focals = [
        { scale: 1, x: '0%', y: '0%' },
        { scale: SCALE, x: '50%', y: '-50%' },
        { scale: SCALE, x: '-1%', y: '-16%' },
        { scale: SCALE, x: '-50%', y: '-40%' },
    ];

    const laptopFocal = {
        zoomOut: { scale: 1, x: '0%', y: '0%' },
        panDown: { scale: 1, x: '0%', y: '-90%' },
        // Keep x at 0 so laptop zoom stays vertical after pan-down (no sideways snap).
        zoomIn: { scale: 5, x: '0%', y: '-330%' },
    };


    // ------------------------------------------------------------------------
    // matchMedia — desktop only
    // ------------------------------------------------------------------------
    const mm = gsap.matchMedia();

    mm.add('(min-width: 1200px)', () => {

        const MOVE = 0.8;
        const PAUSE = 0.2;

        let heroTl = null;
        let hScrollTween = null;

        if (section && banner) {

            // One scrubbed timeline: two timelines on the same banner made tl2’s first
            // tween start from the wrong x (e.g. right focal) → horizontal snap before pan-down.
            heroTl = gsap.timeline({
                scrollTrigger: {
                    trigger: section,
                    start: 'top top',
                    end: 'bottom bottom',
                    scrub: 2.5,
                },
            });

            heroTl.to(banner, { scale: focals[1].scale, x: focals[1].x, y: focals[1].y, duration: MOVE, ease: 'power2.inOut' });
            heroTl.to(banner, { scale: focals[1].scale, x: focals[1].x, y: focals[1].y, duration: PAUSE, ease: 'none' });

            heroTl.to(banner, { scale: focals[2].scale, x: focals[2].x, y: focals[2].y, duration: MOVE, ease: 'power2.inOut' });
            heroTl.to(banner, { scale: focals[2].scale, x: focals[2].x, y: focals[2].y, duration: PAUSE, ease: 'none' });

            heroTl.to(banner, { scale: focals[3].scale, x: focals[3].x, y: focals[3].y, duration: MOVE, ease: 'power2.inOut' });
            heroTl.to(banner, { scale: focals[3].scale, x: focals[3].x, y: focals[3].y, duration: PAUSE, ease: 'none' });

            heroTl.to(banner, { scale: laptopFocal.zoomOut.scale, x: laptopFocal.zoomOut.x, y: laptopFocal.zoomOut.y, duration: MOVE, ease: 'power2.inOut' });

            heroTl.to(banner, { scale: laptopFocal.panDown.scale, x: laptopFocal.panDown.x, y: laptopFocal.panDown.y, duration: MOVE, ease: 'power2.inOut' });
            heroTl.to(banner, { scale: laptopFocal.panDown.scale, x: laptopFocal.panDown.x, y: laptopFocal.panDown.y, duration: PAUSE * 0.5, ease: 'none' });

            heroTl.to(banner, { scale: laptopFocal.zoomIn.scale, x: laptopFocal.zoomIn.x, y: laptopFocal.zoomIn.y, duration: MOVE * 1.5, ease: 'power2.inOut' });
            heroTl.to(banner, { scale: laptopFocal.zoomIn.scale, x: laptopFocal.zoomIn.x, y: laptopFocal.zoomIn.y, duration: PAUSE * 0.3, ease: 'none' });

            if (fadeEl) {
                heroTl.to(fadeEl, {
                    opacity: 1,
                    duration: MOVE * 3,
                    ease: 'power2.inOut',
                });
            }

            if (introPanel) {
                heroTl.to(introPanel, {
                    opacity: 1,
                    duration: MOVE,
                    ease: 'power2.inOut',
                    onComplete: () => { introPanel.style.pointerEvents = 'auto'; },
                    onReverseComplete: () => { introPanel.style.pointerEvents = 'none'; },
                }, '-=0.3');
            }
        }


        // --------------------------------------------------------------------
        // HORIZONTAL SCROLL — 5 panels scroll sideways on vertical scroll
        // --------------------------------------------------------------------
        if (hSection && hTrack && hFrame) {

            const scrollSpan = () => Math.max(0, hTrack.scrollWidth - hFrame.clientWidth);

            hScrollTween = gsap.to(hTrack, {
                x: () => -scrollSpan(),
                ease: 'none',
                scrollTrigger: {
                    trigger: hSection,
                    start: 'top top',
                    end: () => `+=${scrollSpan()}`,
                    scrub: 1,
                    pin: true,
                    anticipatePin: 1,
                    invalidateOnRefresh: true,
                    onUpdate(self) {
                        if (hScrubberFill) {
                            hScrubberFill.style.width = `${self.progress * 100}%`;
                        }
                    },
                },
            });
        }

        // Cleanup
        return () => {
            if (heroTl) heroTl.kill();
            if (hScrollTween) hScrollTween.kill();
            if (banner) gsap.set(banner, { clearProps: 'all' });
            if (fadeEl) gsap.set(fadeEl, { clearProps: 'all' });
            if (introPanel) {
                gsap.set(introPanel, { clearProps: 'all' });
                introPanel.style.pointerEvents = '';
            }
            if (hTrack) gsap.set(hTrack, { clearProps: 'all' });
            if (hScrubberFill) hScrubberFill.style.width = '';
        };
    });

}